import React, { useState, useEffect, useRef } from "react";
import { useDebouncedCallback } from "use-debounce/lib";
import {
  Spinner,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
  Table,
} from "reactstrap";
import { toast } from "react-hot-toast";
import CreatableAsync from "react-select/async-creatable";
import { nullChecker, isLetter } from "~/utils";
import request from "~/utils/request";
import ListIntegrationBidang from "~/components/ListIntegrationBidang";

const EditPemilik = ({ layer, loading: loadingGetData, detailInfo }) => {
  const inputNonWargaRef = useRef();

  const [search, setSearch] = useState("");
  const [nonWarga, setNonWarga] = useState("");
  const [nonWargaNIK, setNonWargaNIK] = useState("");
  const [listPenduduk, setListPenduduk] = useState([]);
  const [listPemilik, setListPemilik] = useState([]);
  const [loadingCount, setLoadingCount] = useState(false);
  const [openModalSelect, setOpenModalSelect] = useState(false);
  const [dataPickPenduduk, setDataPickPenduduk] = useState(null);
  const [modalDetailPenduduk, setModalDetailPenduduk] = useState(false);
  const [loadingCountNonWarga, setLoadingCountNonWarga] = useState(false);
  const [loadingRequestDetailPenduduk, setLoadingRequestDetailPenduduk] =
    useState(false);
  const [isLoading, setIsLoading] = useState({
    loadingPemilik: false,
    loadingPenduduk: false,
  });

  useEffect(() => {
    setIsLoading({ ...isLoading, loadingPenduduk: true });
    request
      .get("/v1/penduduk?include[]=keluarga")
      .then((res) => {
        setListPenduduk(res.data.data);
      })
      .finally(() => {
        setIsLoading({ ...isLoading, loadingPenduduk: false });
      });
  }, []);

  useEffect(() => {
    setIsLoading({
      ...isLoading,
      loadingPenduduk: true,
    });

    let searchQuery = `/v1/penduduk/?filter{nik.contains}=${search}&include[]=keluarga`;

    if (isLetter(search)) {
      searchQuery = `/v1/penduduk/?filter{nama.contains}=${search}&include[]=keluarga`;
    }

    request
      .get(searchQuery)
      .then((res) => setListPenduduk(res.data.data))
      .finally(() =>
        setIsLoading({
          ...isLoading,
          loadingPenduduk: false,
        })
      );
  }, [search]);

  const addPemilik = (penduduk) => {
    const filterPendudukData = Object.entries(penduduk).filter(([key]) =>
      ["nama", "nik", "id"].includes(key)
    );

    setLoadingCount(true);

    request
      .post(`/v1/kepemilikan-count/`, {
        nik: penduduk.nik,
      })
      .then((res) => {
        setListPemilik([
          ...listPemilik,
          {
            ...Object.fromEntries(filterPendudukData),
            is_warga: true,
            is_wajib_pajak: false,
            namabidang: res?.data?.data?.nilai,
          },
        ]);
        setListPenduduk(listPenduduk.filter((p) => p.id !== penduduk.id));
      })
      .finally(() => setLoadingCount(false));
  };

  const deletePemilik = (penduduk) => {
    setListPemilik(
      listPemilik.filter(
        (pemilik) => parseInt(penduduk.nik) !== parseInt(pemilik.nik)
      )
    );

    if (penduduk.is_warga) {
      setListPenduduk([...listPenduduk, penduduk]);
    }
  };

  const handleSubmitPemilik = () => {
    let data = {
      id: layer.feature.properties.id,
      daftar_pemilik: listPemilik?.map((pemilik) => ({
        nama: pemilik.nama,
        namabidang: pemilik.namabidang ?? "",
        is_warga: pemilik.is_warga ?? false,
        is_wajib_pajak: pemilik.is_wajib_pajak ?? false,
        nik: pemilik.nik === "" ? null : pemilik.nik,
      })),
    };

    setIsLoading({ ...isLoading, loadingPemilik: true });

    request
      .patch(`/v1/sig/${layer.feature.properties.id}/`, data)
      .then((res) => {
        const newDataPemilik = res.data.data.daftar_pemilik;
        setListPemilik(newDataPemilik);
        toast.success(
          `Data pemilik bidang NBT ${layer.feature.properties.properties.nama} berhasil di perbaharui !`
        );
      })
      .finally(() => setIsLoading({ ...isLoading, loadingPemilik: false }));
  };

  const getPemilikNonWarga = async (searchValue) => {
    const { data: response } = await request.get(
      `/v1/pemiliknonwarga?search=${searchValue}`
    );

    return response?.data?.map((res) => ({
      label: `${res.nama?.toUpperCase()} - Jumlah Kepemilikan ${
        res?.jumlah_kepemilikan
      }`,
      value: res?.nama,
      nama: res?.nama,
      nik: res?.nik,
    }));
  };

  const onChangeNonWargaNIK = (event) => {
    event.preventDefault();
    setNonWargaNIK(event.target.value);
  };

  const handleAddNonWarga = () => {
    if (nonWarga.length > 0) {
      let pemilikNonWarga = {
        id: Date.now(),
        nama: nonWarga,
        nik: nonWargaNIK,
        is_warga: false,
        is_wajib_pajak: false,
      };

      setLoadingCountNonWarga(true);

      request
        .post(`/v1/kepemilikan-count/`, {
          nik: nonWargaNIK === "" ? null : nonWargaNIK,
        })
        .then((res) => {
          setListPemilik([
            ...listPemilik,
            {
              ...pemilikNonWarga,
              namabidang: res.data.data.nilai,
            },
          ]);
          setNonWarga("");
          setNonWargaNIK("");
          if (inputNonWargaRef.current) {
            inputNonWargaRef.current.focus();
          }
        })
        .finally(() => setLoadingCountNonWarga(false));
    }
  };

  const handleRequestDetailPenduduk = async (penduduk) => {
    try {
      setLoadingRequestDetailPenduduk(true);

      const response = await request.get(
        `/v1/penduduk/${penduduk.id}?include[]=keluarga`
      );

      setDataPickPenduduk(response?.data?.data);
    } finally {
      setLoadingRequestDetailPenduduk(false);
    }
  };

  const debounce = useDebouncedCallback((value) => {
    setSearch(value);
  }, 800);

  useEffect(() => {
    if (detailInfo) {
      const defaultDataPemilik = detailInfo?.daftar_pemilik?.map((pemilik) => ({
        ...pemilik,
        id: Date.now(),
      }));

      setListPemilik(defaultDataPemilik);
    }
  }, [detailInfo]);

  return (
    <React.Fragment>
      <>
        <div style={{ height: "200px", overflowY: "auto" }} className="pl-2">
          <div
            className="form-group mb-0"
            style={{
              top: 0,
              width: "100%",
              position: "sticky",
            }}
          >
            <input
              type="text"
              placeholder="Ketikkan Nama atau Nomor Identitas"
              className="form-control"
              onChange={(e) => debounce.callback(e.target.value?.toLowerCase())}
              style={{
                borderBottomLeftRadius: "0px",
                borderBottomRightRadius: "0px",
              }}
            />
          </div>
          <table className="table table-borderless mt-3 table-sm">
            {isLoading.loadingPenduduk ? (
              <tr>
                <td>
                  <Spinner className="d-block mt-3 mx-auto" size="md" />
                </td>
              </tr>
            ) : listPenduduk?.length > 0 ? (
              listPenduduk?.map((penduduk) => (
                <tr className="mb-3" key={penduduk.id}>
                  <td className="align-middle">
                    {penduduk.nama?.toUpperCase()} - {penduduk.nik}
                  </td>
                  <td width="10%">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => {
                        setModalDetailPenduduk(true);
                        handleRequestDetailPenduduk(penduduk);
                      }}
                    >
                      <i className="mdi mdi-information" />
                    </button>
                  </td>
                  <td width="10%">
                    {loadingCount &&
                    JSON.stringify(penduduk) ===
                      JSON.stringify(dataPickPenduduk) ? (
                      <button disabled className="btn btn-sm btn-primary">
                        <Spinner size="sm" />
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => addPemilik(penduduk)}
                      >
                        &gt;&gt;
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <div className="text-center mt-4">
                <i className="mdi mdi-information-outline" /> tidak ada data
                penduduk di desa ini
              </div>
            )}
          </table>
        </div>
        <div
          className="alert alert-warning text-center"
          style={{ fontSize: "10px" }}
        >
          Jika pemilik <strong>bukan warga</strong>, maka admin dapat mengentri
          nama pemilik di kolom di bawah ini:
        </div>
        <div className="mx-2">
          <div className="input-group">
            <input
              value={nonWarga}
              className="form-control"
              placeholder="Ketikkan Nama..."
              onClick={() => setOpenModalSelect(!openModalSelect)}
            />
            <input
              type="number"
              value={nonWargaNIK}
              ref={inputNonWargaRef}
              className="form-control"
              onChange={onChangeNonWargaNIK}
              placeholder="Ketikkan NIK..."
            />
            <div className="input-group-append">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleAddNonWarga}
                disabled={nonWarga === "" || loadingCountNonWarga}
              >
                {loadingCountNonWarga ? <Spinner size="sm" /> : "Tambah"}
              </button>
            </div>
          </div>
        </div>
        <div
          className="mt-3 pl-2 pb-5"
          style={{ maxHeight: "170px", overflowY: "auto" }}
        >
          {loadingGetData ? (
            <Spinner className="d-block mt-3 mx-auto" size="sm" />
          ) : (
            listPemilik?.map((pemilik, index) => (
              <ListIntegrationBidang
                key={pemilik.id}
                count={index + 1}
                name={pemilik.nama}
                data-pemilik_id={pemilik.id}
                value={pemilik.namabidang}
                onDelete={() => deletePemilik(pemilik)}
                onChange={(e) => {
                  const pemilik_id = Number(e.target.dataset.pemilik_id);
                  const namabidang = e.target.value;

                  const addNamaBidang = listPemilik?.map((pemilik) =>
                    pemilik.id === pemilik_id
                      ? { ...pemilik, namabidang }
                      : pemilik
                  );

                  setListPemilik(addNamaBidang);
                }}
              />
            ))
          )}
        </div>
        <div
          className="simpanPerubahan"
          style={{ position: "absolute", left: 0, right: 0, bottom: -55 }}
        >
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmitPemilik}
            disabled={isLoading.loadingPemilik}
            style={{
              textTransform: "uppercase",
              position: "absolute",
              left: "0",
              right: 0,
              bottom: "0",
              display: "block",
              width: "100%",
              height: "50px",
              fontSize: "1rem",
            }}
          >
            {isLoading.loadingPemilik ? "menyimpan data..." : "Simpan"}
          </button>
        </div>

        {/* MODAL DETAIL KELUARGA (PENGUASA) */}

        <Modal
          centered
          isOpen={modalDetailPenduduk}
          toggle={() => setModalDetailPenduduk(false)}
        >
          <ModalHeader className="text-uppercase">
            Detail Penduduk {dataPickPenduduk?.nama ?? "-"}
          </ModalHeader>

          <ModalBody>
            {loadingRequestDetailPenduduk ? (
              <div className="py-5">
                <Spinner className="d-block mx-auto" />
              </div>
            ) : (
              <div>
                <div className="d-flex mb-3">
                  <p className="font-weight-bold" style={{ width: "40%" }}>
                    NIK
                  </p>
                  <p style={{ width: "10%" }}>:</p>
                  <p style={{ width: "50%" }}>
                    {nullChecker(dataPickPenduduk?.nik)}
                  </p>
                </div>

                <div className="d-flex mb-3">
                  <p className="font-weight-bold" style={{ width: "40%" }}>
                    Alamat Lengkap
                  </p>
                  <p style={{ width: "10%" }}>:</p>
                  <p style={{ width: "50%" }}>
                    {nullChecker(dataPickPenduduk?.alamat_lengkap)}
                  </p>
                </div>

                <div className="d-flex mb-3">
                  <p className="font-weight-bold" style={{ width: "40%" }}>
                    Status Dalam Keluarga:
                  </p>
                  <p style={{ width: "10%" }}>:</p>
                  <p style={{ width: "50%" }}>
                    {nullChecker(dataPickPenduduk?.status_dalam_keluarga)}
                  </p>
                </div>

                <div className="d-flex mb-3">
                  <p className="font-weight-bold" style={{ width: "40%" }}>
                    Keluarga
                  </p>
                  <p style={{ width: "10%" }}>:</p>
                </div>

                <Table striped>
                  <thead>
                    <tr>
                      <th>Nama Kepala Keluarga</th>
                      <th>Nomor Kartu Keluarga</th>
                      <th>Alamat Lengkap</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>
                        {nullChecker(
                          dataPickPenduduk?.keluarga?.kepala_keluarga?.nama
                        )}
                      </td>
                      <td>{nullChecker(dataPickPenduduk?.keluarga?.no_kk)}</td>
                      <td>
                        {nullChecker(
                          dataPickPenduduk?.keluarga?.alamat_lengkap
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
          </ModalBody>
        </Modal>

        <Modal
          isOpen={openModalSelect}
          centered
          toggle={() => setOpenModalSelect(!openModalSelect)}
        >
          <ModalHeader>Pilih atau Buat Nama</ModalHeader>

          <ModalBody>
            <CreatableAsync
              cacheOptions
              defaultOptions
              placeholder="Pilih atau Buat Nama"
              loadOptions={getPemilikNonWarga}
              onChange={(val) => {
                setNonWarga(val?.value);
                setNonWargaNIK(val?.nik ?? "");
              }}
            />
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              color="danger"
              className="mr-2"
              onClick={() => setOpenModalSelect(!openModalSelect)}
            >
              Batal
            </Button>

            <Button
              type="button"
              color="success"
              onClick={() => setOpenModalSelect(!openModalSelect)}
            >
              Pilih
            </Button>
          </ModalFooter>
        </Modal>
      </>
    </React.Fragment>
  );
};

export default EditPemilik;
